import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, CircularProgress, Stack, Typography} from 'views/components';
import {Translate} from 'views/module';

import {getMessageMethod, sortMessages} from '../../communicationUtils';
import InternalComposer from './InternalComposer';
import InternalMessage from './InternalMessage';

import backend from 'utils/backend';

const InternalList = (props) => {
  const {
    selectedParticipantKind,
    selectedParticipantResourceRn,
  } = props;
  const requestIdRef = React.useRef(0);
  const [messages, setMessages] = React.useState([]);
  const [lastEvaluatedKey, setLastEvaluatedKey] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [loadFailed, setLoadFailed] = React.useState(false);

  const loadMessages = React.useCallback(async (startKey = null, append = false) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!selectedParticipantResourceRn || !['CLIENT', 'CAREGIVER'].includes(selectedParticipantKind)) {
      setMessages([]);
      setLastEvaluatedKey(null);
      setLoadFailed(false);
      return;
    }

    setLoading(true);
    setLoadFailed(false);

    try {
      const output = selectedParticipantKind === 'CLIENT' ?
        await backend.client.message.load(selectedParticipantResourceRn, startKey, true, true) :
        await backend.caregiver.message.load(selectedParticipantResourceRn, startKey, true, true);
      const nextMessages = output?.messages || [];

      if (requestId !== requestIdRef.current) return;

      setMessages((currentMessages) => append ? [...currentMessages, ...nextMessages] : nextMessages);
      setLastEvaluatedKey(output?.lastEvaluatedKey || null);
    } catch {
      if (requestId !== requestIdRef.current) return;

      if (!append) {
        setMessages([]);
        setLastEvaluatedKey(null);
      }
      setLoadFailed(true);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [selectedParticipantKind, selectedParticipantResourceRn]);

  React.useEffect(() => {
    loadMessages(null, false);
  }, [loadMessages]);

  const onMessageCreated = React.useCallback((createdMessage) => {
    if (!createdMessage?.messageRn) return;

    setMessages((currentMessages) => [
      createdMessage,
      ...currentMessages.filter((message) => message.messageRn !== createdMessage.messageRn),
    ]);
  }, []);

  const onClickLoadMore = () => {
    if (!lastEvaluatedKey || loading) return;
    loadMessages(lastEvaluatedKey, true);
  };

  const visibleMessages = React.useMemo(() => {
    return sortMessages(messages).filter((message) => getMessageMethod(message.messageType) === 'INTERNAL');
  }, [messages]);

  return (
    <Stack sx={{gap: 1.25}}>
      <InternalComposer
        onMessageCreated={onMessageCreated}
        selectedParticipantKind={selectedParticipantKind}
        selectedParticipantResourceRn={selectedParticipantResourceRn}
      />
      {
        loading && messages.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 3,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )
      }
      {
        loadFailed && (
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 400,
              color: '#D85D31',
            }}
          >
            <Translate expression='case.Communication.LoadFailed' />
          </Typography>
        )
      }
      {
        !loading && !loadFailed && visibleMessages.length === 0 && (
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 400,
              color: '#6D7280',
            }}
          >
            <Translate expression='case.Communication.EmptyMessages' />
          </Typography>
        )
      }
      {
        visibleMessages.map((message) => (
          <InternalMessage
            key={message.messageRn}
            message={message}
          />
        ))
      }
      {
        lastEvaluatedKey && (
          <Button
            disabled={loading}
            onClick={onClickLoadMore}
            sx={{
              'height': '32px',
              'border': '1px solid #DCDDE5',
              'borderRadius': '7px',
              'backgroundColor': '#FFFFFF',
              'color': '#6D7280',
              'textTransform': 'none',
              'boxShadow': 'none',
              '&:hover': {
                'border': '1px solid #D1D3DD',
                'backgroundColor': '#F7F8FA',
                'boxShadow': 'none',
              },
            }}
            variant='outlined'
          >
            <Translate expression='case.Communication.LoadMore' />
          </Button>
        )
      }
    </Stack>
  );
};

InternalList.propTypes = {
  selectedParticipantKind: PropTypes.string,
  selectedParticipantResourceRn: PropTypes.string,
};

export default React.memo(InternalList);
